<template>
  <generic-modal
    @close="$emit('close')"
    :show-actions="false"
    :title="'Ticket details for ' + selectedTicket.title"
    width="600px"
  >
    <div class="tw-flex tw-flex-column tw-gap-5">
      <div class="tw-flex tw-mt-2">
        <span>Ticket ID: </span>
        <span class="tw-font-bold tw-ml-2">
          <!-- TODO: replace hardcoded URL with ticketsBaseUrl from '@/logic/common/urls' -->
          <a
            :href="`https://tickets.norm.eu/v2/tickets/${selectedTicket?.id}`"
            class="hover:tw-underline tw-text-l tw-cursor-pointer tw-px-2"
            target="_blank"
          >
            {{ selectedTicket.id }}
          </a></span
        >
      </div>
      <div class="tw-flex tw-mt-2">
        <span>Status: </span>
        <span class="tw-font-bold tw-ml-2">{{ selectedTicket.status }}</span>
      </div>
      <div class="tw-flex tw-mt-2">
        <span>Partner: </span>
        <span class="tw-font-bold tw-ml-2 tw-mb-2">{{ selectedTicket.partner }}</span>
      </div>
    </div>
    <div class="tw-flex tw-flex-column tw-gap-5">
      <div class="tw-flex tw-mt-2">
        <form-field
          class="tw-font-bold tw-mb-2"
          label="Prioriteit"
          :options="priority"
          type="select"
          v-model="editForm.priority"
          @change="updateTicket({ priority: editForm.priority })"
        />
      </div>
      <div class="tw-flex tw-mt-2">
        <form-field
          class="tw-font-bold tw-ml-2 tw-mb-2"
          label="Ref number"
          v-model="editForm.refNumber"
          @change="updateTicket({ refNumber: editForm.refNumber })"
        />
      </div>
    </div>
    <div class="tw-flex tw-flex-column tw-gap-5">
      <div class="tw-flex tw-mt-2">
        <form-field
          class="tw-font-bold tw-mb-2"
          label="Categorie"
          :options="categories"
          type="select"
          v-model="editForm.category"
          @change="updateTicket({ category: editForm.category })"
        />
      </div>
      <div class="tw-flex tw-mt-2">
        <form-field
          class="tw-font-bold tw-ml-2 tw-mb-2"
          label="Maintenance Type"
          :options="maintenanceTypes"
          type="select"
          v-model="editForm.maintenanceType"
          @change="updateTicket({ maintenanceType: editForm.maintenanceType })"
        />
      </div>
    </div>
    <div class="tw-my-2">
      <form-field
        class="tw-font-bold tw-mb-2 tw-flex-full"
        label="Omschrijving"
        type="textarea"
        v-model="editForm.description"
        rows="3"
      />
      <button
        class="tw-bg-green-600 tw-text-white tw-p-2 tw-rounded tw-w-full disabled:tw-bg-gray-200"
        :disabled="editForm.description === selectedTicket.description"
        @click="updateTicket({ description: editForm.description })"
      >
        Update omschrijving
      </button>
    </div>
    <div
      class="tw-flex tw-flex-col tw-w-full"
      v-if="selectedTicket.status !== 'cancelled' && selectedTicket.status !== 'approved'"
    >
      <form-field
        hideLabel="true"
        type="textarea"
        label="Comment/remark"
        rows="2"
        v-model="comment"
        placeholder="Comment/remark"
      />
      <div
        class="tw-flex tw-my-3 tw-w-full tw-gap-3"
        v-if="selectedTicket.status === 'partner_approved'"
      >
        <button
          class="tw-bg-green-600 tw-text-white tw-p-2 tw-rounded disabled:tw-bg-gray-200 tw-w-1/2"
          @click="approveTicket(selectedTicket)"
        >
          Goedkeuren
        </button>
        <button
          class="tw-bg-red-500 tw-text-white tw-p-2 tw-rounded disabled:tw-bg-gray-200 tw-w-1/2"
          :disabled="!comment"
          @click="rejectTicket(selectedTicket)"
        >
          Afkeuren
        </button>
      </div>

      <button
        class="tw-bg-red-500 tw-text-white tw-p-2 tw-rounded disabled:tw-bg-gray-200"
        :disabled="!comment"
        v-if="selectedTicket.status !== 'cancelled' || selectedTicket.status !== 'approved'"
        @click="cancelTicket(selectedTicket)"
      >
        Cancel
      </button>
    </div>
  </generic-modal>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import fetchData from '@/util/fetch'
import { notifications } from '@/util'
import FormField from '@/components/shared/FormField.vue'
import GenericModal from '@/components/shared/GenericModal.vue'

const props = defineProps({
  caseDetail: {
    type: Object,
    required: true,
  },
  selectedTicket: {
    type: Object,
    required: true,
  },
})

const queryClient = useQueryClient()
const selectedTicket = computed(() => props.selectedTicket)
const comment = ref('')

const maintenanceTypes = [
  'Storing',
  'Service',
  'Lekkage',
  'Schadeherstel',
  'Herstel inspectie',
  'Installatiegarantie',
]
const categories = ['O&M', 'KAM', 'OPS', 'Klantenservice', 'Alarmbellen']
const priority = ['Leak (6 hours)', 'Urgent', 'Normal']

const { data: currentTicketDetail } = useQuery({
  queryKey: computed(() => ['currentTicket', selectedTicket.value?.id]),
  queryFn: async () => {
    const url = `/tickets/api/v1/tickets/${selectedTicket.value.id}`
    const result = await fetchData(url)
    return result.data
  },
  refetchOnWindowFocus: false,
  enabled: true,
})

watch(currentTicketDetail, () => {
  editForm.value.maintenanceType =
    currentTicketDetail.value.data.input.finance.malfunction_category || ''
  editForm.value.refNumber = currentTicketDetail.value.data.input.finance.client_reference || ''
})

const editForm = ref({
  priority: selectedTicket?.value?.priority,
  description: selectedTicket?.value?.description,
  category: selectedTicket?.value?.category,
  maintenanceType: currentTicketDetail?.value?.data?.input.finance.malfunction_category,
  refNumber: currentTicketDetail?.value?.data?.input.finance.client_reference,
})

const { mutate: updateTicket } = useMutation({
  mutationFn: async (body) => {
    const url = `/tickets/api/v1/tickets/${selectedTicket.value?.id}`

    const result = await fetchData(url, {
      method: 'PUT',
      body: JSON.stringify(body),
    })

    return result.data
  },
  onSuccess: () => {
    notifications.addNotification({
      message: `Ticket ${selectedTicket.value?.id} updated successfully`,
      type: 'success',
    })

    queryClient.invalidateQueries({ queryKey: ['cases'] })
    queryClient.invalidateQueries({ queryKey: ['tickets'] })
  },
  onError: (error) => {
    notifications.addNotification({
      message: `Something went wrong while updating ticket ${selectedTicket.value?.id}: ${error.message}`,
      type: 'danger',
    })
  },
})

const cancelTicket = () => {
  comment.value = ''
  updateTicket({ signal: 'ecorus_cancel', cancel_comment: 'Cancelled by NORM' })
}

const approveTicket = () => {
  updateTicket({ signal: 'ecorus_approve', approve_remark: comment.value })
  comment.value = ''
}

const rejectTicket = () => {
  updateTicket({ signal: 'ecorus_reject', reject_remark: comment.value })
  comment.value = ''
}
</script>
